import React, { useRef, useState } from "react";
import * as htmlToImage from "html-to-image";
import { Button } from "./ui/button";
import { Download, Share2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const PODCAST_COVER =
    "/assets/podcast-cover.png";

const SITE_URL = "walkwithpurposeco.site";

const SHARE_TEXT = "I'm becoming. Becoming H.E.R.: Breaking the Cycles — a faith-centered podcast with Coach Melissa.";

export const ShareMyBecoming = ({ savedEpisodes, savedTracks, onShare }) => {
    const cardRef = useRef(null);
    const [busy, setBusy] = useState(null); // 'download' | 'share' | 'copy'

    const total = savedEpisodes.length + savedTracks.length;

    const generate = async () => {
        if (!cardRef.current) return null;
        return await htmlToImage.toPng(cardRef.current, {
            cacheBust: true,
            pixelRatio: 2,
            backgroundColor: "#2B1D35",
            skipFonts: true,
        });
    };

    const handleDownload = async () => {
        try {
            setBusy("download");
            const data = await generate();
            if (!data) return;
            const a = document.createElement("a");
            a.href = data;
            a.download = "my-becoming.png";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            trackEvent("share_card_downloaded", { items: total });
            toast.success("Saved. Now post it to your story 💜");
            onShare?.();
        } catch {
            toast.error("Couldn't generate the image. Try again.");
        } finally {
            setBusy(null);
        }
    };

    const handleShare = async () => {
        try {
            setBusy("share");
            const data = await generate();
            if (!data) return;
            const blob = await (await fetch(data)).blob();
            const file = new File([blob], "my-becoming.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: "My Becoming",
                    text: SHARE_TEXT,
                });
                trackEvent("share_card_shared", { items: total });
                onShare?.();
            } else {
                // fallback to download
                await handleDownload();
            }
        } catch (err) {
            if (err && err.name !== "AbortError") {
                toast.error("Sharing wasn't available. Downloaded instead.");
                await handleDownload();
            }
        } finally {
            setBusy(null);
        }
    };

    const handleCopy = async () => {
        try {
            setBusy("copy");
            await navigator.clipboard.writeText(`${SHARE_TEXT}\n${SITE_URL}`);
            trackEvent("share_card_text_copied");
            toast.success("Caption copied. Paste it anywhere.");
        } catch {
            toast.error("Couldn't copy.");
        } finally {
            setBusy(null);
        }
    };

    if (total === 0) return null;

    return (
        <section data-testid="share-collection-section" className="pt-2">
            <div className="flex items-center justify-between mb-3">
                <div className="eyebrow text-lilac-700 flex items-center gap-2">
                    <Share2 className="w-3.5 h-3.5" /> Share your collection
                </div>
            </div>

            {/* Live preview (scaled down) */}
            <div className="relative rounded-3xl overflow-hidden border border-lilac-200 bg-white p-3">
                <div
                    style={{
                        width: 270,
                        height: 338,
                        margin: "0 auto",
                        transform: "scale(1)",
                        transformOrigin: "top center",
                    }}
                >
                    <ShareCard
                        ref={cardRef}
                        savedEpisodes={savedEpisodes}
                        savedTracks={savedTracks}
                        scale={0.25}
                    />
                </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
                <Button
                    onClick={handleShare}
                    disabled={busy !== null}
                    data-testid="share-btn-share"
                    className="rounded-full h-10 px-3 bg-gold hover:bg-gold-deep text-white text-xs"
                >
                    {busy === "share" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                    Share
                </Button>
                <Button
                    onClick={handleDownload}
                    disabled={busy !== null}
                    variant="outline"
                    data-testid="share-btn-download"
                    className="rounded-full h-10 px-3 border-lilac-300 text-lilac-900 text-xs"
                >
                    {busy === "download" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Save
                </Button>
                <Button
                    onClick={handleCopy}
                    disabled={busy !== null}
                    variant="outline"
                    data-testid="share-btn-copy"
                    className="rounded-full h-10 px-3 border-lilac-300 text-lilac-900 text-xs"
                >
                    {busy === "copy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                    Caption
                </Button>
            </div>
            <p className="mt-3 text-xs text-lilac-700/80 leading-relaxed">
                Share to Instagram, Threads, or anywhere — every share helps another woman find the becoming.
            </p>
        </section>
    );
};

// ============================================================================
// Hidden template — full 1080×1350 portrait that gets rendered to PNG
// ============================================================================
const ShareCard = React.forwardRef(({ savedEpisodes, savedTracks, scale = 1 }, ref) => {
    const W = 1080;
    const H = 1350;
    const items = [
        ...savedEpisodes.slice(0, 4).map((e) => ({
            kind: "EP " + String(e.number).padStart(2, "0"),
            title: e.title,
        })),
        ...savedTracks.slice(0, 3).map((t) => ({
            kind: t.track_type || "Album",
            title: t.title,
        })),
    ].slice(0, 5);

    return (
        <div
            ref={ref}
            style={{
                width: W * scale,
                height: H * scale,
                position: "relative",
                background: "linear-gradient(155deg, #2B1D35 0%, #3F2655 50%, #5D3E74 100%)",
                color: "#FAF5FC",
                fontFamily: "'Outfit', sans-serif",
                overflow: "hidden",
                borderRadius: 24 * scale,
            }}
        >
            {/* Gold orb */}
            <div
                style={{
                    position: "absolute",
                    width: 600 * scale,
                    height: 600 * scale,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(230,193,88,0.50) 0%, rgba(230,193,88,0) 70%)",
                    top: -200 * scale,
                    right: -160 * scale,
                    filter: `blur(${60 * scale}px)`,
                }}
            />
            <div
                style={{
                    position: "absolute",
                    width: 500 * scale,
                    height: 500 * scale,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(199,168,214,0.35) 0%, rgba(199,168,214,0) 70%)",
                    bottom: -180 * scale,
                    left: -140 * scale,
                    filter: `blur(${60 * scale}px)`,
                }}
            />

            {/* Top: eyebrow */}
            <div
                style={{
                    position: "absolute",
                    top: 80 * scale,
                    left: 80 * scale,
                    right: 80 * scale,
                    fontSize: 24 * scale,
                    letterSpacing: 8 * scale,
                    textTransform: "uppercase",
                    color: "#E6C158",
                    fontWeight: 500,
                }}
            >
                My Becoming · Saved Collection
            </div>

            {/* Headline */}
            <div
                style={{
                    position: "absolute",
                    top: 180 * scale,
                    left: 80 * scale,
                    right: 80 * scale,
                    fontFamily: "'Bodoni Moda', Georgia, serif",
                    fontSize: 110 * scale,
                    lineHeight: 1.0,
                    fontWeight: 500,
                    letterSpacing: -1.5 * scale,
                }}
            >
                I'm{" "}
                <span style={{ fontStyle: "italic", color: "#E6C158" }}>becoming.</span>
            </div>

            {/* Items */}
            <div
                style={{
                    position: "absolute",
                    top: 460 * scale,
                    left: 80 * scale,
                    right: 80 * scale,
                    display: "flex",
                    flexDirection: "column",
                    gap: 22 * scale,
                }}
            >
                {items.map((it, i) => (
                    <div
                        key={i}
                        style={{
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(230,193,88,0.20)",
                            borderRadius: 28 * scale,
                            padding: `${22 * scale}px ${28 * scale}px`,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6 * scale,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 18 * scale,
                                letterSpacing: 4 * scale,
                                textTransform: "uppercase",
                                color: "#E6C158",
                                fontWeight: 500,
                            }}
                        >
                            {it.kind}
                        </div>
                        <div
                            style={{
                                fontFamily: "'Bodoni Moda', Georgia, serif",
                                fontSize: 36 * scale,
                                lineHeight: 1.15,
                                color: "#FAF5FC",
                                fontWeight: 500,
                            }}
                        >
                            {it.title}
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div
                        style={{
                            fontFamily: "'Bodoni Moda', Georgia, serif",
                            fontStyle: "italic",
                            fontSize: 38 * scale,
                            color: "rgba(250,245,252,0.85)",
                            lineHeight: 1.3,
                        }}
                    >
                        "Real conversations. Raw healing. Kingdom transformation."
                    </div>
                )}
            </div>

            {/* Bottom: podcast cover + signature */}
            <div
                style={{
                    position: "absolute",
                    bottom: 80 * scale,
                    left: 80 * scale,
                    right: 80 * scale,
                    display: "flex",
                    alignItems: "center",
                    gap: 30 * scale,
                }}
            >
                <img
                    src={PODCAST_COVER}
                    alt=""
                    crossOrigin="anonymous"
                    style={{
                        width: 130 * scale,
                        height: 130 * scale,
                        borderRadius: 24 * scale,
                        objectFit: "cover",
                        border: `${2 * scale}px solid rgba(230,193,88,0.45)`,
                    }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontFamily: "'Bodoni Moda', Georgia, serif",
                            fontSize: 44 * scale,
                            lineHeight: 1.05,
                            color: "#FAF5FC",
                            fontWeight: 500,
                        }}
                    >
                        Becoming{" "}
                        <span style={{ fontStyle: "italic", color: "#E6C158" }}>H.E.R.</span>
                    </div>
                    <div
                        style={{
                            marginTop: 4 * scale,
                            fontSize: 22 * scale,
                            color: "rgba(250,245,252,0.75)",
                        }}
                    >
                        Breaking the Cycles · with Coach Melissa
                    </div>
                    <div
                        style={{
                            marginTop: 10 * scale,
                            fontSize: 20 * scale,
                            letterSpacing: 4 * scale,
                            textTransform: "uppercase",
                            color: "#E6C158",
                        }}
                    >
                        {SITE_URL}
                    </div>
                </div>
            </div>
        </div>
    );
});
ShareCard.displayName = "ShareCard";
